// src/pages/CourseTime/components/PollResultsModal.comp.jsx
import React, { useEffect, useState } from 'react';
import * as S from '../styles';
import PollResultsChart from './PollResultsChart.comp';
import { fetchPollResults } from '../../../services/learning_program.service';
import { GET_APP_LOCALIZE_TEXT } from '../../../utils/dq_lc_service/lc_service';

const PollResultsModal = ({ isOpen, onClose, module_code, session, segment, slide, pollType, question, options, themeColor, pageLc }) => {
  const [pollData, setPollData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch poll results when modal opens
  useEffect(() => {
    if (isOpen && module_code && session && segment && slide !== undefined && options) {
      fetchPollData();
    }
  }, [isOpen, module_code, session, segment, slide, options]);

  const fetchPollData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchPollResults(module_code, session, segment, slide);
      console.log('API Response received:', response);
      
      // Handle different response structures - check if response has data directly or nested
      const responseData = response.data || response;
      
      if (responseData && (responseData.options !== undefined || responseData.totalResponses !== undefined)) {
        // Merge frontend options with API statistics
        const apiStats = responseData.options || [];
        const totalResponses = responseData.totalResponses || 0;
        
        // Create a map of API statistics by index
        const statsMap = {};
        apiStats.forEach(stat => {
          statsMap[stat.index] = {
            count: stat.count,
            percentage: stat.percentage
          };
        });
        
        // Create full options array with text from frontend and stats from API
        const mergedOptions = options.map((text, index) => ({
          text: text,
          count: statsMap[index]?.count || 0,
          percentage: statsMap[index]?.percentage || 0
        }));
        
        const enhancedData = {
          module_code,
          session,
          segment,
          slide,
          pollType,
          question,
          totalResponses,
          options: mergedOptions
        };
        
        console.log('Poll data processed successfully:', enhancedData);
        setPollData(enhancedData);
      } else {
        console.error('API response failed or invalid:', response);
        setError('Invalid poll results data received');
      }
    } catch (err) {
      console.error('Error fetching poll results:', err);
      setError('Unable to load poll results. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    setPollData(null);
    setError(null);
    onClose();
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <S.ModalOverlay onClick={handleBackdropClick}>
      <S.ModalContainer onClick={(e) => e.stopPropagation()}>
        <S.ModalHeader>
          <S.ModalTitle themeColor={themeColor}>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__poll_results')}</S.ModalTitle>
          <S.ModalCloseButton onClick={handleClose} aria-label="Close modal">
            ×
          </S.ModalCloseButton>
        </S.ModalHeader>
        
        <S.ModalContent>
          {loading && (
            <S.ModalLoading>
              <S.LoadingSpinner />
              <S.LoadingText>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__loading_poll_results')}...</S.LoadingText>
            </S.ModalLoading>
          )}
          
          {error && (
            <S.ErrorContainer>
              <S.ErrorIcon>⚠️</S.ErrorIcon>
              <S.ErrorMessage>{error}</S.ErrorMessage>
              <S.RetryButton onClick={fetchPollData} themeColor={themeColor}>
                {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__try_again')}
              </S.RetryButton>
            </S.ErrorContainer>
          )}
          
          {pollData && !loading && !error && (
            <PollResultsChart pollData={pollData} pageLc={pageLc} />
          )}
        </S.ModalContent>
        
        <S.ModalFooter>
          <S.ModalFooterNote>
            {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__poll_results_note')}
          </S.ModalFooterNote>
        </S.ModalFooter>
      </S.ModalContainer>
    </S.ModalOverlay>
  );
};

export default PollResultsModal;
